import { create } from 'zustand';
import { UserInfo, NotificationPreferences, NotificationFrequency } from '@/shared/types/api';
import { ApiClient, tokenStorage, refreshAccessToken, setAuthFailureHandler, getErrorMessage } from '@/shared/services/api';

interface AuthState {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  notificationPreferences: NotificationPreferences | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>;
  provisionUser: (email: string, firstName: string, lastName: string) => Promise<string | null>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  updateProfile: (firstName: string, lastName: string, email: string, atlassianAccountId?: string) => Promise<{ success: boolean; emailConfirmationRequired: boolean }>;
  confirmEmailChange: (userId: string, token: string) => Promise<{ success: boolean; message?: string }>;
  loadNotificationPreferences: () => Promise<void>;
  updateNotificationPreferences: (emailNotifications: boolean, notificationFrequency: NotificationFrequency) => Promise<boolean>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  (set) => ({
    // Initial state. isLoading starts true so guarded routes show a spinner
    // during the boot refresh instead of flashing a redirect to /login.
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    notificationPreferences: null,

    // Actions
    login: async (email: string, password: string) => {
      set({ isLoading: true, error: null });

      try {
        const result = await ApiClient.login({ Email: email, Password: password });

        if (result.success && result.token) {
          // Refresh token arrives as an HttpOnly cookie; only the access token is in the body.
          tokenStorage.setToken(result.token);

          const user = await ApiClient.getCurrentUser();

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          return true;
        } else {
          const errorMessage = result.message || result.errors.join(', ') || 'Login failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          return false;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error, 'Login failed');
        set({
          isLoading: false,
          error: errorMessage,
        });
        return false;
      }
    },

    provisionUser: async (email: string, firstName: string, lastName: string) => {
      try {
        const result = await ApiClient.register({ Email: email, FirstName: firstName, LastName: lastName });

        if (result.success) {
          return null;
        }

        return result.message || result.errors.join(', ') || 'Failed to create user';
      } catch (error) {
        return getErrorMessage(error, 'Failed to create user');
      }
    },

    logout: async () => {
      set({ isLoading: true });

      try {
        await ApiClient.logout();
      } catch (error) {
        // Continue with logout even if API call fails
        console.error('Logout API call failed:', error);
      }

      tokenStorage.clearTokens();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    },

    initializeAuth: async () => {
      // On a cold load the access token is gone from memory. Attempt a silent
      // refresh using the HttpOnly cookie; success recovers the session.
      set({ isLoading: true });

      try {
        await refreshAccessToken();
        const user = await ApiClient.getCurrentUser();
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch {
        tokenStorage.clearTokens();
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    },

    updateProfile: async (firstName: string, lastName: string, email: string, atlassianAccountId?: string) => {
      set({ isLoading: true, error: null });

      try {
        const result = await ApiClient.updateUserProfile({ firstName, lastName, email, atlassianAccountId });

        if (result.success && result.user) {
          // Update user info in the store with the returned user data. On an email change the
          // returned email is still the current (unconfirmed change) one, which is what we want.
          set({
            user: result.user,
            isLoading: false,
            error: null,
          });

          return { success: true, emailConfirmationRequired: result.emailConfirmationRequired ?? false };
        } else {
          const errorMessage = result.message || (result.errors && result.errors.length > 0 ? result.errors.join(', ') : 'Profile update failed');
          set({
            isLoading: false,
            error: errorMessage,
          });
          return { success: false, emailConfirmationRequired: false };
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error, 'Profile update failed');
        set({
          isLoading: false,
          error: errorMessage,
        });
        return { success: false, emailConfirmationRequired: false };
      }
    },

    confirmEmailChange: async (userId: string, token: string) => {
      try {
        const result = await ApiClient.confirmEmailChange({ UserId: userId, Token: token });

        if (result.success && result.token) {
          // The API rotated the session: revoked the old tokens and issued a fresh pair (refresh
          // token as an HttpOnly cookie, access token in the body). Adopt it so the user lands
          // signed in with their new email, then load the updated profile.
          tokenStorage.setToken(result.token);
          const user = await ApiClient.getCurrentUser();

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          return { success: true, message: result.message };
        }

        return { success: false, message: result.message || result.errors.join(', ') || 'This confirmation link is invalid or has expired.' };
      } catch (error) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        return { success: false, message: err.response?.data?.message || err.message || 'This confirmation link is invalid or has expired.' };
      }
    },

    loadNotificationPreferences: async () => {
      try {
        const result = await ApiClient.getNotificationPreferences();
        if (result.success && result.preferences) {
          set({ notificationPreferences: result.preferences });
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      }
    },

    updateNotificationPreferences: async (emailNotifications, notificationFrequency) => {
      set({ isLoading: true, error: null });

      try {
        const result = await ApiClient.updateNotificationPreferences({ emailNotifications, notificationFrequency });

        if (result.success && result.preferences) {
          set({ notificationPreferences: result.preferences, isLoading: false, error: null });
          return true;
        }

        const errorMessage = result.message || (result.errors && result.errors.length > 0 ? result.errors.join(', ') : 'Failed to update notification preferences');
        set({ isLoading: false, error: errorMessage });
        return false;
      } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to update notification preferences');
        set({ isLoading: false, error: errorMessage });
        return false;
      }
    },

    clearError: () => set({ error: null }),
    setLoading: (loading: boolean) => set({ isLoading: loading }),
  })
);

// Let the API layer reset auth state on a failed refresh (breaks the api<->store import cycle).
setAuthFailureHandler(() => {
  useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false });
});